import type { ConsoleBrandDetail, ConsoleRevision } from '@rsecure/brand-contracts/console';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Badge, ErrorState, Loading, Ltr, Notice, StatusBadge, Time } from '../../components/ui';
import { api } from '../../lib/api';
import { diffRevisions } from '../../lib/diff';

function Swatch({ v }: { v: string | null }) {
  if (!v) return <span className="faint">—</span>;
  return (
    <span>
      {/^#[0-9a-f]{6}$/i.test(v) && <i className="swatch" style={{ background: v }} />}
      <Ltr mono>{v}</Ltr>
    </span>
  );
}

function Compare({ a, b }: { a: ConsoleRevision; b: ConsoleRevision }) {
  const { fields, assets } = diffRevisions(a, b);
  const changed = fields.filter((f) => f.change !== 'same');
  const changedAssets = assets.filter((x) => x.change !== 'same');
  return (
    <div className="grid" style={{ gap: 14 }} data-testid="revision-compare">
      {changed.length === 0 && changedAssets.length === 0 && (
        <Notice>No differences in compared fields</Notice>
      )}
      {changed.length > 0 && (
        <div className="table-wrap">
          <table className="t diff">
            <thead>
              <tr>
                <th scope="col">Group</th>
                <th scope="col">Field</th>
                <th scope="col">r#{a.number}</th>
                <th scope="col">r#{b.number}</th>
                <th scope="col">Change</th>
              </tr>
            </thead>
            <tbody>
              {changed.map((f) => (
                <tr key={f.field + (f.before ?? '') + (f.after ?? '')}>
                  <td className="muted">{f.group}</td>
                  <td className="mono">{f.field}</td>
                  <td className="old">
                    {f.group === 'colors' ? <Swatch v={f.before} /> : (f.before ?? '—')}
                  </td>
                  <td className="new">
                    {f.group === 'colors' ? <Swatch v={f.after} /> : (f.after ?? '—')}
                  </td>
                  <td>
                    <Badge
                      tone={f.change === 'added' ? 'ok' : f.change === 'removed' ? 'bad' : 'warn'}
                    >
                      {f.change}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="asset-grid">
        {assets.map((x) => (
          <div className="asset" key={x.role}>
            <div className="pv">
              {(
                [
                  ['before', x.before],
                  ['after', x.after],
                ] as const
              ).map(([k, side], i) => (
                <div
                  key={k}
                  style={{
                    background: '#fff',
                    borderInlineStart: i ? '1px solid var(--border)' : undefined,
                  }}
                >
                  {side?.url ? (
                    <img src={side.url} alt={`${x.role} r#${i ? b.number : a.number}`} />
                  ) : (
                    <span className="faint" style={{ fontSize: 12 }}>
                      {side ? 'not public' : 'none'}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="meta">
              <div className="row">
                <strong className="mono">{x.role}</strong>
                <Badge
                  tone={
                    x.change === 'same'
                      ? 'neutral'
                      : x.change === 'added'
                        ? 'ok'
                        : x.change === 'removed'
                          ? 'bad'
                          : 'warn'
                  }
                >
                  {x.change === 'changed' ? 'logo updated' : x.change}
                </Badge>
              </div>
              <div className="muted mono" style={{ fontSize: 11.5 }}>
                {x.before?.sha256.slice(0, 10) ?? '—'} → {x.after?.sha256.slice(0, 10) ?? '—'}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function HistoryTab({ d }: { d: ConsoleBrandDetail }) {
  const q = useQuery({
    queryKey: ['revisions', d.entity.slug],
    queryFn: () => api.revisions(d.entity.slug),
  });
  const revs = q.data?.revisions ?? [];
  const [baseId, setBase] = useState<string | null>(null);
  const [targetId, setTarget] = useState<string | null>(null);
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Loading />;
  if (!revs.length)
    return (
      <Notice tone="warn">
        This entity has no revisions — identity verification has not produced normalized content.
      </Notice>
    );
  const target = revs.find((r) => r.id === targetId) ?? revs.find((r) => r.active) ?? revs[0];
  const base =
    revs.find((r) => r.id === baseId) ?? revs.find((r) => r.number < target.number) ?? null;
  return (
    <div className="grid" style={{ gap: 16 }}>
      <Notice>Read-only: rollback is available only through the audited CLI/admin API.</Notice>
      <section className="card">
        <div className="card-b flush table-wrap">
          <table className="t" data-testid="revision-table">
            <thead>
              <tr>
                <th scope="col">Revision</th>
                <th scope="col">State</th>
                <th scope="col">Source</th>
                <th scope="col">Created</th>
                <th scope="col">Approved</th>
                <th scope="col">Published</th>
                <th scope="col">Superseded</th>
                <th scope="col">Triggering run</th>
                <th scope="col">Reviews</th>
              </tr>
            </thead>
            <tbody>
              {revs.map((r) => (
                <tr key={r.id} className={r.active ? 'selected' : undefined}>
                  <td className="nowrap">
                    <strong>#{r.number}</strong> {r.active && <Badge tone="ok">active</Badge>}
                    <div className="faint mono" style={{ fontSize: 11 }}>
                      {r.normalizedSha256.slice(0, 12)}
                    </div>
                  </td>
                  <td>
                    <StatusBadge kind="revision" value={r.state} />
                  </td>
                  <td>{r.sourceKind ?? '—'}</td>
                  <td>
                    <Time iso={r.createdAt} />
                  </td>
                  <td>
                    {r.approvedBy ? (
                      <>
                        <span className="mono" style={{ fontSize: 12 }}>
                          {r.approvedBy}
                        </span>
                        <div
                          className="muted"
                          style={{ fontSize: 12 }}
                          title={r.approvalReason ?? ''}
                        >
                          {(r.approvalReason ?? '').slice(0, 80)}
                        </div>
                      </>
                    ) : r.rejectedReason ? (
                      <span className="err">{r.rejectedReason}</span>
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td>
                    <Time iso={r.publishedAt} />
                  </td>
                  <td>
                    <Time iso={r.supersededAt} />
                  </td>
                  <td>
                    {r.createdByRun ? (
                      <Link
                        className="link mono"
                        to={`/operations/runs/${r.createdByRun.id}`}
                        style={{ fontSize: 12 }}
                      >
                        {r.createdByRun.kind} · {r.createdByRun.trigger}
                      </Link>
                    ) : (
                      <span
                        className="faint"
                        title="No normalize run recorded this revision id (e.g. historical import)"
                      >
                        not recorded
                      </span>
                    )}
                  </td>
                  <td>
                    {r.reviews.length ? (
                      r.reviews.map((v) => v.kind).join(', ')
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card" aria-labelledby="h-pubev">
        <div className="card-h">
          <h2 id="h-pubev">Publication &amp; rollback events</h2>
        </div>
        <div className="card-b flush table-wrap">
          {q.data.publicationEvents.length ? (
            <table className="t">
              <tbody>
                {q.data.publicationEvents.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <Badge tone={e.action === 'rollback' ? 'warn' : 'ok'}>{e.action}</Badge>
                    </td>
                    <td className="nowrap">
                      {e.fromRevisionNumber ? `r#${e.fromRevisionNumber} → ` : ''}r#
                      {e.toRevisionNumber}
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {e.actor}
                    </td>
                    <td className="muted">{e.reason}</td>
                    <td>
                      <Time iso={e.createdAt} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="muted" style={{ padding: 16, margin: 0 }}>
              No publication events.
            </p>
          )}
        </div>
      </section>

      <section className="card" aria-labelledby="h-compare">
        <div className="card-h">
          <h2 id="h-compare">Compare revisions</h2>
          {revs.length > 1 && (
            <div style={{ display: 'flex', gap: 8 }}>
              <label className="muted" style={{ fontSize: 12.5 }}>
                Base{' '}
                <select
                  className="select"
                  value={base?.id ?? ''}
                  onChange={(e) => setBase(e.target.value)}
                >
                  {revs.map((r) => (
                    <option key={r.id} value={r.id}>
                      r#{r.number} · {r.state}
                    </option>
                  ))}
                </select>
              </label>
              <label className="muted" style={{ fontSize: 12.5 }}>
                Compare with{' '}
                <select
                  className="select"
                  value={target.id}
                  onChange={(e) => setTarget(e.target.value)}
                >
                  {revs.map((r) => (
                    <option key={r.id} value={r.id}>
                      r#{r.number} · {r.state}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>
        <div className="card-b">
          {base && base.id !== target.id ? (
            <Compare a={base} b={target} />
          ) : (
            <p className="muted" style={{ margin: 0 }}>
              Only one revision exists — nothing to compare yet.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
